import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { httpOptions } from '../constants/http-options.constant';
import { ApiMyProfile, MyProfile, adaptMyProfile, ChangeEmailRequest, ChangeEmailResult } from '../models';
import { DebugLoggerService } from './debug-logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthnProfileService {
  private _http = inject(HttpClient);
  private _debugLogger = inject(DebugLoggerService);
  private baseUrl = environment.baseUrlAuthN;

  // Stato condiviso del profilo: popolato da loadProfile() (chiamato dal parent
  // AccountSettingsComponent). Consumato dai child (ProfileView, ChangePassword
  // gating) e da AccountSettingsComponent stesso per il loading skeleton.
  private readonly _profile = signal<MyProfile | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal(false);
  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /**
   * Idempotent: scatena UN solo fetch del profilo. Successive chiamate (da
   * parent o altri consumer) sono no-op finché c'è già profilo o un fetch
   * in corso. Aggiorna i signal `loading`, `error`, `profile` di conseguenza.
   */
  loadProfile(): void {
    if (this._loading() || this._profile()) {
      return;
    }
    this._loading.set(true);
    this._error.set(false);
    this.getMyProfile().subscribe({
      next: () => this._loading.set(false),
      error: () => {
        this._loading.set(false);
        this._error.set(true);
      }
    });
  }

  getMyProfile(): Observable<MyProfile> {
    const url = `${this.baseUrl}/v1/me/profile`;
    const start = performance.now();

    return this._http.get<ApiMyProfile>(url, httpOptions).pipe(
      tap(response => this._debugLogger.logHttp('GET', url, null, response, Math.round(performance.now() - start))),
      map(adaptMyProfile),
      tap(profile => this._profile.set(profile)),
      catchError(error => {
        this._debugLogger.logHttpError('GET', url, error, Math.round(performance.now() - start));
        return throwError(() => error);
      })
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const url = `${this.baseUrl}/v1/me/change-password`;
    const body = { currentPassword, newPassword };
    const start = performance.now();

    return this._http.post<void>(url, body, httpOptions).pipe(
      tap(() => this._debugLogger.logHttp('POST', url, { currentPassword: '***', newPassword: '***' }, null, Math.round(performance.now() - start))),
      catchError(error => {
        this._debugLogger.logHttpError('POST', url, error, Math.round(performance.now() - start));
        return throwError(() => error);
      })
    );
  }

  changeEmail(newEmail: string): Observable<ChangeEmailResult> {
    const url = `${this.baseUrl}/v1/me/change-email`;
    const body: ChangeEmailRequest = { newEmail };
    const start = performance.now();

    return this._http.post<ChangeEmailResult>(url, body, httpOptions).pipe(
      tap(response => {
        this._debugLogger.logHttp('POST', url, { newEmail }, response, Math.round(performance.now() - start));
        // Invalida cache profilo: prossimo loadProfile rifetcha dal BE.
        this._profile.set(null);
      }),
      catchError(error => {
        this._debugLogger.logHttpError('POST', url, error, Math.round(performance.now() - start));
        return throwError(() => error);
      })
    );
  }
}
